import Link from "next/link"
import { MapPin, Calendar, Clock } from "lucide-react"
import type { Post } from "@/lib/posts"
import ImageWithFallback from "@/components/image-with-fallback"
import { cn, slugify } from "@/lib/utils"
import Image from "next/image"

interface PostCardProps {
  post: Post
  variant?: "default" | "compact" | "featured"
  className?: string
}

export default function PostCard({ post, variant = "default", className }: PostCardProps) {
  const isFeatured = variant === "featured"
  const isCompact = variant === "compact"

  return (
    <div className={cn("group", className)}>
      <div
        className={cn(
          "rounded-xl overflow-hidden shadow-sm transition-all duration-300 hover:shadow-md hover:-translate-y-1 bg-card h-full",
          isFeatured && "md:grid md:grid-cols-2 md:gap-6",
        )}
      >
        {/* Image section with link */}
        <Link href={`/blog/${post.slug}`} className="block">
          <div className={cn("relative", isFeatured ? "h-64 md:h-full" : isCompact ? "h-32" : "h-48 md:h-56")}>
            <Image
              src={post.coverImage || "/placeholder.svg"}
              alt={post.title}
              fill
              className="object-cover group-hover:scale-105 transition-transform duration-300"
            />

            {post.featured && !isCompact && (
              <span className="absolute top-4 right-4 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                Featured
              </span>
            )}
          </div>
        </Link>

        {/* Content section */}
        <div className={cn("p-5 md:p-6", isFeatured && "flex flex-col justify-center")}>
          <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
            {post.location && (
              <span className="flex items-center gap-1">
                <MapPin className="w-4 h-4" />
                {typeof post.location === 'string' ? post.location : post.location.name}
              </span>
            )}
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {post.date}
            </span>
            {!isCompact && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.readingTime} min
              </span>
            )}
          </div>

          {/* Title with link */}
          <Link href={`/blog/${post.slug}`}>
            <h3
              className={cn(
                "font-bold mb-2 group-hover:text-primary transition-colors line-clamp-2 cursor-pointer",
                isFeatured ? "text-2xl" : isCompact ? "text-base" : "text-xl",
              )}
            >
              {post.title}
            </h3>
          </Link>

          {!isCompact && (
            <Link href={`/blog/${post.slug}`}>
              <p className="text-muted-foreground mb-4 line-clamp-2 cursor-pointer">{post.excerpt}</p>
            </Link>
          )}

          {/* Tags */}
          {!isCompact && post.tags && Array.isArray(post.tags) && post.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {post.tags.slice(0, 3).map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center rounded-full bg-muted text-muted-foreground text-xs px-2 py-0.5 font-medium"
                >
                  #{tag}
                </span>
              ))}
              {post.tags.length > 3 && (
                <span className="text-xs text-muted-foreground">
                  +{post.tags.length - 3} more
                </span>
              )}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ImageWithFallback
                src={post.author.avatar || "/placeholder.svg"}
                alt={post.author.name}
                width={32}
                height={32}
                className="rounded-full"
              />
              <span className="text-sm font-medium">{post.author.name}</span>
            </div>

            {/* Category badge - separate link */}
            {!isCompact && post.categories && Array.isArray(post.categories) && post.categories.length > 0 && (
              <Link
                href={`/category/${slugify(post.categories[0])}`}
                className="inline-flex items-center rounded-full bg-primary/10 text-primary hover:bg-primary/20 text-sm px-3 py-1 font-medium transition-colors"
              >
                {post.categories[0]}
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
